import type { RideProductVersion } from "@tdelivr/api-client/generated/mobility/ride-product-catalog.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What the service catalogue does to the products MOB-01 returns, away from any rendering.
 *
 * **This screen exists because R-12's rejection of it was wrong, and wrong in a useful way.** That
 * entry asked of each of the design's fifteen pages whether an operation answers a list of the
 * things the page is a list of, and rejected `catalogue` under "the service does not exist": the
 * design cites `CAT-01` and `CAT-02`, and the catalogue of 142 published contracts answers to
 * neither. Both halves of that are still true. What it did not ask is whether some *other* service
 * answers the page's own question — and MOB-01 `GET /v1/ride-products` does, publicly, tenant-
 * scoped, on one read scope, publishing `displayName`, `vehicleClass`, `travelMode`,
 * `passengerCapacity`, `luggageCapacity`, `serviceAreaIds` and `lifecycle` per row.
 *
 * Two of the design's three columns are then exact and the third is the design's own grammar:
 * `4 seats · 2 bags` is `passengerCapacity` and `luggageCapacity`, written the way the design writes
 * them. R-17 in `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 *
 * **Two findings, and both are false statements rather than blanks.** They are `lifecycleIsConstant`
 * and `areaListIsNotEnforced` below.
 */

/**
 * MOB-01's listing can only ever answer `active`, and the contract says otherwise.
 *
 * The repository behind `GET /v1/ride-products`:
 *
 * ```sql
 * SELECT DISTINCT ON (product_id) product_state FROM ride_product_versions
 *  WHERE tenant_id=$1 AND lifecycle='active'
 *    AND effective_from<=$2 AND (effective_until IS NULL OR effective_until>$2)
 *  ORDER BY product_id, version DESC LIMIT 1000
 * ```
 *
 * The `lifecycle` column and the `lifecycle` inside `product_state` are written from the same value
 * in the same insert, so every row this listing answers carries `lifecycle: "active"` — while the
 * contract publishes the field as the enum `draft | active | retired`. The field is a constant on
 * this operation and a three-value enum on the other two.
 *
 * **The consequence is not a blank cell.** A draft product and a retired one do not appear at all,
 * and nothing in the response says so: no count, no total, no echo of the `at` the caller sent. A
 * page titled "Service catalogue" that answers "your classes" with the subset bookable at one
 * instant, under a state column that reads `active` on every row, is a complete-looking catalogue
 * with records missing from it — and the one column that could have warned the reader confirms the
 * opposite. An administrator who has just created a draft class and does not find it here will
 * reasonably conclude the create failed.
 *
 * So the board does not hold the field out as a state an administrator chose. It draws the
 * contract's word, says on every badge that the listing admits no other, and says under the grid
 * what the listing is a list of.
 */
export const LIFECYCLE_IS_CONSTANT =
  "MOB-01's listing selects `lifecycle='active'` and the effective window, so every row it answers reads `active` and no draft or retired product appears at all. The field is published as a three-value enum and is a constant on this operation.";

/**
 * `serviceAreaIds` restricts nothing, and the design's badge says it restricts three zones.
 *
 * MOB-01 publishes `serviceAreaIds` on every row — up to 500 service-area identifiers — and
 * **no service on this platform acts on it.** Searched across every `src/` in `services/` and
 * `packages/`: outside MOB-01's own contract, domain, routes and tests, the occurrences are the
 * generated client's type, a `serviceAreaIds: []` in one ride-quote test fixture, and EXP-004, which
 * resolves each identifier to the zone GEO-06 names and refuses nothing on the strength of it.
 * Dispatch, quoting and GEO-06's availability evaluation do not consult it. It is written, stored,
 * published, now readable by name, and still never enforced.
 *
 * The distinction is the whole of the badge's claim and it was not available to make when this was
 * first measured, because nothing read the field at all. A reader that turns the identifiers into
 * names tells an administrator *which* zones are listed; an enforcer would make the listing mean
 * something. Only the second would change the badge's words, and EXP-004 is deliberately the first:
 * `tests/catalogue.test.ts` holds its files to naming no dispatch, quote or availability decision.
 *
 * **The design draws `caution · 3 zones only` on exactly that field, and its own form says "Leave
 * empty for all served zones".** A badge saying a class is restricted to three zones would tell an
 * administrator that a restriction they configured is in force, while the platform offers that class
 * everywhere — and the error lands on a customer: a Boda offered in a zone the operator
 * deliberately excluded. That is strictly worse than an empty cell, which is why the badge carries
 * the measured fact instead.
 */
export const AREA_LIST_IS_NOT_ENFORCED =
  "The product names service areas and no service on this platform acts on `serviceAreaIds`, so the list restricts nothing: this class is offered wherever the tenant operates.";

/** The cap the ride-product repository applies, read out of its own query rather than assumed. */
export const PRODUCT_CAP = 1000;

/**
 * The design's `Type` cell.
 *
 * `ride`, on every row, and the constant is the point. The design's two values are `ride` and
 * `delivery`; MOB-01 is the ride product catalogue and holds no delivery type, and DEL-01 — which
 * does — publishes **no listing at all**: its only read is
 * `GET /v1/tenants/{tenantId}/delivery-products/{productId}/effective`, which needs a `productId`
 * the page exists to discover *and* a `geographyId` no operation on this platform answers. So half
 * the design's column has a source and half does not, and the half that does says `ride`.
 */
export function typeText(): string {
  return "ride";
}

/**
 * The design's `Capacity` cell, in the design's own grammar.
 *
 * `4 seats · 2 bags`, `1 seat` — the design's two sample forms, singular and plural, with the bags
 * clause dropped when there are none, because `1 seat · 0 bags` is not a sentence the design writes
 * and `luggageCapacity` is a non-negative integer that can be zero.
 */
export function capacityText(product: RideProductVersion): string {
  const seats = `${String(product.passengerCapacity)} ${product.passengerCapacity === 1 ? "seat" : "seats"}`;
  if (product.luggageCapacity === 0) return seats;
  return `${seats} · ${String(product.luggageCapacity)} ${product.luggageCapacity === 1 ? "bag" : "bags"}`;
}

/**
 * The state class and the word.
 *
 * `active` is the contract's word where the design writes `published`, for the reason `zones.ts`
 * gives about its own three: the published value is what the service will say about this row
 * tomorrow, and a prettier synonym chosen here is a second vocabulary nobody else on the platform
 * uses.
 *
 * A product naming service areas takes `caution` — the class whose label is "Qualified", which is
 * exactly a configuration that is recorded and not enforced — where the design has `caution` for
 * its own restricted row. The word is the measured one rather than the design's `3 zones only`, for
 * the reason `AREA_LIST_IS_NOT_ENFORCED` states.
 *
 * The two unreachable lifecycles are still handled rather than asserted away: this function is also
 * what an export and a test see, and MOB-01's other two reads do answer `draft` and `retired`.
 */
export function stateOf(product: RideProductVersion): { classKey: StateClassKey; label: string } {
  if (product.lifecycle === "draft") return { classKey: "idle", label: "draft" };
  if (product.lifecycle === "retired") return { classKey: "closed", label: "retired" };
  if (product.serviceAreaIds.length > 0) {
    return { classKey: "caution", label: "area list not enforced" };
  }
  return { classKey: "done", label: "active" };
}

/** Why the badge says what it says, where the design has no column for it. */
export function stateReason(product: RideProductVersion): string {
  if (product.serviceAreaIds.length > 0) {
    return `${String(product.serviceAreaIds.length)} service ${product.serviceAreaIds.length === 1 ? "area is" : "areas are"} named on this product. ${AREA_LIST_IS_NOT_ENFORCED} ${LIFECYCLE_IS_CONSTANT}`;
  }
  return LIFECYCLE_IS_CONSTANT;
}

/**
 * How this class travels, where MOB-01 published it.
 *
 * On the row's `title` rather than in a column, because the design has three columns and this is
 * not one of them. It is worth carrying at all because `travelMode` is the one field MOB-01 derives
 * rather than stores — from `vehicleClass`, so that a class with no mapping cannot be made active —
 * and it is published as **optional**: absent on a draft whose class the service cannot map and on
 * any version written before the derivation existed. A board that read it as required would print
 * `undefined` on such a row.
 */
export function rowDetail(product: RideProductVersion): string {
  const parts = [`${product.productId} · v${String(product.version)}`, product.vehicleClass];
  parts.push(
    product.travelMode === undefined
      ? "travel mode not derived — MOB-01 publishes it as optional and could not map this vehicle class"
      : `travels by ${product.travelMode}`,
  );
  if (product.description !== undefined && product.description.length > 0) {
    parts.push(product.description);
  }
  return parts.join(" · ");
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type CatalogueOrder = "default" | "state" | "az" | "za";

export interface CatalogueQuery {
  readonly search: string;
  readonly order: CatalogueOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
  /** The state chosen in the toolbar's filter, or `undefined` for all of them. */
  readonly state?: string;
}

export interface CataloguePage {
  readonly rows: readonly RideProductVersion[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly RideProductVersion[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(product: RideProductVersion): string {
  return [
    product.displayName,
    typeText(),
    capacityText(product),
    stateOf(product).label,
    product.productId,
    product.vehicleClass,
    product.travelMode ?? "",
    product.description ?? "",
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is the order MOB-01 answered in — `ORDER BY product_id, version DESC`, so by
 * identifier. That order is not meaningful to an administrator and it is still the default, for the
 * reason the zones board keeps its own: a board that silently re-sorts has told the reader
 * something about the service that is not true.
 *
 * The filter is over the **state on screen**, computed here, because MOB-01's listing takes exactly
 * one query parameter — `at`, an instant — and no lifecycle filter. A filter that refetched would
 * be asking a different question.
 */
export function cataloguePage(
  rows: readonly RideProductVersion[],
  query: CatalogueQuery,
): CataloguePage {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (product) =>
      (query.state === undefined || stateOf(product).label === query.state) &&
      (needle.length === 0 || searchableText(product).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? matched
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left).label.localeCompare(stateOf(right).label);
          }
          const compared = left.displayName.localeCompare(right.displayName);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/**
 * The one sentence this board owes an administrator about what it fetched.
 *
 * On every render, because what it says is always true: this listing is not a list of the tenant's
 * classes, it is a list of the classes bookable at one instant, and nothing in the response says
 * how many were left out. `LIFECYCLE_IS_CONSTANT` is the measurement; this is the sentence an
 * administrator reads.
 *
 * @param fetched how many products came back, or `undefined` before anything has.
 */
export function fetchNote(fetched: number | undefined): string | undefined {
  if (fetched === undefined) return undefined;
  const count = `${String(fetched)} ${fetched === 1 ? "class" : "classes"}`;
  return `The ${count} MOB-01 answers as effective now. Its listing selects only products whose lifecycle is active and whose effective window contains this instant, and publishes no count of the rest, so drafts and retired classes are absent and this board cannot say how many.`;
}
